import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { KeyRound, LoaderCircle, LogOut, ShieldCheck, Smartphone } from 'lucide-react'
import type { SsoProvider } from '@takeit4me/shared'
import { useAuth } from '../../auth'
import { publicApi } from '../../api'
import { enrolTotp, answerChallenge, verifyCode } from '../../lib/mfa'
import { subdomainCompany } from '../../lib/company'
import { AuthShell, FormError, PROVIDER_LABELS } from './AuthShell'

/** After an invite or a reset link: choose a password (the link signed the person in). */
export function SetPasswordPage() {
  const auth = useAuth()
  const nav = useNavigate()
  const [pw, setPw] = useState('')
  const [pw2, setPw2] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  if (auth.status === 'loading') return <AuthShell title="Choose a password"><div className="text-muted flex items-center gap-2"><LoaderCircle className="spin" size={15} /> Checking your link…</div></AuthShell>
  if (auth.status === 'signed_out') {
    return (
      <AuthShell title="This link has expired" sub="Invite and reset links work once, for a limited time">
        <div className="text-ink2 text-[12.5px] mb-4">Ask your admin to resend your invite, or request a new reset link from the sign-in page.</div>
        <Link to="/login" className="btn pri w-full h-10 justify-center">Go to sign in</Link>
      </AuthShell>
    )
  }
  const tooShort = pw.length > 0 && pw.length < 10
  return (
    <AuthShell title="Choose a password" sub={auth.session?.user.email ?? undefined}>
      <form onSubmit={async (e) => {
        e.preventDefault()
        if (pw !== pw2) { setError('The two passwords are different.'); return }
        setBusy(true); setError(null)
        try { await auth.setPassword(pw); nav('/today', { replace: true }) } catch (err) { setError(err instanceof Error ? err.message : String(err)) } finally { setBusy(false) }
      }}>
        <label className="label" htmlFor="npw">New password</label>
        <input id="npw" className="input w-full h-10 mb-1" type="password" autoComplete="new-password" required minLength={10} value={pw} onChange={(e) => setPw(e.target.value)} />
        <div className={`text-[11px] mb-3 ${tooShort ? 'text-red-600' : 'text-faint'}`}>At least 10 characters. A phrase is easier to remember.</div>
        <label className="label" htmlFor="npw2">Type it again</label>
        <input id="npw2" className="input w-full h-10 mb-4" type="password" autoComplete="new-password" required value={pw2} onChange={(e) => setPw2(e.target.value)} />
        <FormError>{error}</FormError>
        <button className="btn pri w-full h-10" disabled={busy || pw.length < 10}>{busy && <LoaderCircle className="spin" />} Save and continue</button>
      </form>
    </AuthShell>
  )
}

function CodeInput({ value, onChange, autoFocus }: { value: string; onChange: (v: string) => void; autoFocus?: boolean }) {
  return (
    <input className="input w-full h-11 text-center text-[20px] tracking-[0.4em] font-bold" inputMode="numeric" autoComplete="one-time-code" maxLength={6} autoFocus={autoFocus}
      aria-label="6-digit code" placeholder="000000" value={value} onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 6))} />
  )
}

/** The second step of signing in: a code from the authenticator app (session aal1 → aal2). */
export function MfaChallengePage() {
  const auth = useAuth()
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  return (
    <AuthShell title="Enter your code" sub="Open your authenticator app">
      <form onSubmit={async (e) => {
        e.preventDefault()
        setBusy(true); setError(null)
        try { await answerChallenge(code); await auth.mfaDone() } catch (err) { setError(err instanceof Error ? err.message : String(err)); setCode('') } finally { setBusy(false) }
      }}>
        <div className="text-ink2 text-[12.5px] mb-3 flex gap-2"><Smartphone size={16} className="shrink-0 mt-0.5 text-brand" /> Your company asks for a second step when you sign in. Enter the 6-digit code for takeit4me.</div>
        <CodeInput value={code} onChange={setCode} autoFocus />
        <div className="h-3" />
        <FormError>{error}</FormError>
        <button className="btn pri w-full h-10" disabled={busy || code.length !== 6}>{busy && <LoaderCircle className="spin" />} Verify</button>
      </form>
      <SignOutLink />
    </AuthShell>
  )
}

/** Enrolment of an authenticator app: QR code, then a code to prove it works. Used at sign-in and on the account page. */
export function TotpEnrol({ onDone, onCancel }: { onDone: () => void; onCancel?: () => void }) {
  const [factor, setFactor] = useState<{ factorId: string; qr: string; secret: string } | null>(null)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    let live = true
    enrolTotp().then((f) => { if (live) setFactor(f) }, (e) => { if (live) setError(e instanceof Error ? e.message : String(e)) })
    return () => { live = false }
  }, [])
  if (!factor) return error ? <FormError>{error}</FormError> : <div className="text-muted flex items-center gap-2 py-6 justify-center"><LoaderCircle className="spin" size={15} /> Preparing…</div>
  return (
    <form onSubmit={async (e) => {
      e.preventDefault()
      setBusy(true); setError(null)
      try { await verifyCode(factor.factorId, code); onDone() } catch (err) { setError(err instanceof Error ? err.message : String(err)); setCode('') } finally { setBusy(false) }
    }}>
      <ol className="text-ink2 text-[12.5px] list-decimal pl-5 mb-3 space-y-1">
        <li>Open an authenticator app (Google Authenticator, Microsoft Authenticator, 1Password…).</li>
        <li>Scan this code, or type the key below.</li>
        <li>Enter the 6-digit code it shows.</li>
      </ol>
      <div className="flex justify-center mb-2"><img src={factor.qr} alt="QR code for your authenticator app" width={176} height={176} className="rounded-lg border border-line bg-white p-1" /></div>
      <div className="text-center mb-3"><code className="code text-[11.5px] break-all select-all">{factor.secret}</code></div>
      <CodeInput value={code} onChange={setCode} />
      <div className="h-3" />
      <FormError>{error}</FormError>
      <div className="flex gap-2">
        {onCancel && <button type="button" className="btn flex-1 h-10" onClick={onCancel}>Cancel</button>}
        <button className="btn pri flex-1 h-10" disabled={busy || code.length !== 6}>{busy && <LoaderCircle className="spin" />} Turn on</button>
      </div>
    </form>
  )
}

/** The company requires multi-factor login and this person has no authenticator yet. */
export function MfaEnrolPage() {
  const auth = useAuth()
  return (
    <AuthShell title="Set up a second step" sub="Required by your company" width={420}>
      <div className="text-ink2 text-[12.5px] mb-4 flex gap-2"><ShieldCheck size={16} className="shrink-0 mt-0.5 text-brand" /> {auth.block?.message || 'Your company asks everyone to use an authenticator app when they sign in. It takes a minute.'}</div>
      <TotpEnrol onDone={() => void auth.mfaDone()} />
      <SignOutLink />
    </AuthShell>
  )
}

/** The company requires single sign-on and this sign-in used a password or link. */
export function SsoRequiredPage() {
  const auth = useAuth()
  const [providers, setProviders] = useState<{ list: SsoProvider[]; domain: string | null } | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    const company = subdomainCompany()
    const email = auth.session?.user.email ?? null
    publicApi.loginOptions(company ? { company } : { email }).then((o) => setProviders({ list: o.sso_providers, domain: o.saml_domain }), () => setProviders({ list: [], domain: null }))
  }, [auth.session?.user.email])
  const go = async (p: SsoProvider) => {
    setError(null)
    try {
      await auth.signOut()
      if (p === 'saml') await auth.signInWithSaml(providers?.domain ?? auth.session?.user.email?.split('@')[1] ?? '')
      else await auth.signInWithProvider(p)
    } catch (e) { setError(e instanceof Error ? e.message : String(e)) }
  }
  return (
    <AuthShell title="Use single sign-on" sub="Required by your company">
      <div className="text-ink2 text-[12.5px] mb-4">{auth.block?.message || 'Your company signs in with single sign-on. Sign in again with your company account.'}</div>
      <FormError>{error}</FormError>
      <div className="flex flex-col gap-2">
        {providers === null && <div className="text-muted flex items-center gap-2"><LoaderCircle className="spin" size={15} /> Loading…</div>}
        {providers?.list.map((p) => <button key={p} className="btn w-full h-10 justify-center" onClick={() => void go(p)}><KeyRound /> {PROVIDER_LABELS[p]}</button>)}
        {providers && providers.list.length === 0 && <div className="text-muted text-[12.5px]">Your admin has not finished setting up single sign-on. Ask them to check Settings → Security.</div>}
      </div>
      <SignOutLink />
    </AuthShell>
  )
}

function SignOutLink() {
  const auth = useAuth()
  return <div className="text-center mt-4"><button className="text-muted text-[12px] inline-flex items-center gap-1 hover:text-ink" onClick={() => void auth.signOut()}><LogOut size={13} /> Sign in as someone else</button></div>
}
